import { ControlBar, ErrorBanner, UpgradeCard } from '@/components';
import TrackingPageLayout from '@/components/Layout/TrackingPageLayout';
import { LINK, PATH } from '@/constants';
import { AccountPlan, Device } from '@/constants/enum';
import { isLockFeature, isUndefinedArray } from '@/helpers';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import { useConfirmationNavigate, usePlanDisplayName, useUpgradeTracking } from '@/hooks';
import Appearance from '@/pages/TrackingPage/components/EDD/components/ModalTrackingPage/components/Appearance';
import Lookup from '@/pages/TrackingPage/components/EDD/components/ModalTrackingPage/components/Lookup';
import PageStatus from '@/pages/TrackingPage/components/EDD/components/ModalTrackingPage/components/PageStatus';
import { getMediaUploadRequests } from '@/pages/TrackingPage/components/LookAndFeel/components/ChooseTheme/mediaUpload';
import CustomToast from '@/components/Toast';
import {
  ContentContainerStyled,
  FullscreenBarContainerStyled,
} from '@/pages/TrackingPage/components/EDD/components/ModalTrackingPage/styled';
import {
  useUpdateTrackingPageMutation,
  useUpdateTranslationMutation,
  useUpdateUploadMediaMutation,
  useUploadMediaMutation,
} from '@/redux/api/api.caller';
import { tokenSelector } from '@/redux/features/auth.slice';
import commonSlice from '@/redux/features/common.slice';
import planSlice, { accountSelector } from '@/redux/features/plan.slice';
import trackingPageSlice, {
  languageSelector,
  TrackingPagePreviewPath,
  trackingPageSelector,
} from '@/redux/features/trackingPage.slice';
import trackingPageMediaSlice, {
  isEnableSaveMediaSelector,
  trackingPageMediaSelector,
} from '@/redux/features/trackingPageMedia.slice';
import { Button, ButtonGroup, FullscreenBar, Icon, Layout, Modal, Select, Spinner, Text, Tooltip } from '@shopify/polaris';
import { DesktopIcon, MobileIcon, StatusIcon, ThemeEditIcon, ViewIcon, WandIcon } from '@shopify/polaris-icons';
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTrackingPageUrl from '@/hooks/useTrackingPageUrl';

const PreviewContent = lazy(
  () => import('@/pages/TrackingPage/components/EDD/components/ModalTrackingPage/components/PreviewContent'),
);

export enum TrackingPagePath {
  Lookup = 'lookup',
  Status = 'status',
  Appearance = 'appearance',
}

const navList = [
  {
    title: 'Order lookup page',
    id: TrackingPagePath.Lookup,
    icon: ThemeEditIcon,
    path: TrackingPagePath.Lookup,
  },
  {
    title: 'Order status page',
    id: TrackingPagePath.Status,
    icon: StatusIcon,
    path: TrackingPagePath.Status,
  },
  {
    title: 'Theme settings',
    id: TrackingPagePath.Appearance,
    icon: WandIcon,
    path: TrackingPagePath.Appearance,
  },
];

const ModalTrackingPage = () => {
  const navigate = useConfirmationNavigate();
  const { navigateToPlan } = useUpgradeTracking();
  const { getPlanName } = usePlanDisplayName();
  const trackingPage = useSelector(trackingPageSelector);
  const {
    value: {
      lookAndFeel: {
        content: { shippingInfo, progressBar },
      },
    },
    currentPreviewPath,
    currentPreviewMode,
  } = trackingPage;
  const language = useSelector(languageSelector);
  const dispatch = useDispatch();
  const { shop, token } = useSelector(tokenSelector);
  const { proxyPath } = useTrackingPageUrl();
  const trackingPageMediaData = useSelector(trackingPageMediaSelector);
  const isEnableSaveMedia = useSelector(isEnableSaveMediaSelector);
  const accountState = useSelector(accountSelector);

  const [currentPath, setCurrentPath] = useState(TrackingPagePath.Lookup);
  const [apiError, setApiError] = useState<string>('');
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);

  const [uploadImage, uploadData] = useUploadMediaMutation();
  const [updateUploadImage] = useUpdateUploadMediaMutation();
  const [updateTrackingPage, { isLoading: isLoadingUpdateTrackingPage }] = useUpdateTrackingPageMutation();
  const [updateTranslation, { isLoading: isLoadingUpdateTranslation }] = useUpdateTranslationMutation();

  const handleChangeViewMode = useCallback(
    (index: Device) => {
      if (currentPreviewMode === index) return;
      dispatch(trackingPageSlice.actions.handleCurrentPreviewMode(index));
    },
    [currentPreviewMode, dispatch],
  );

  const handleTabChange = useCallback((path: TrackingPagePath) => setCurrentPath(path), []);

  const renderContentLeft = useCallback(() => {
    switch (currentPath) {
      case TrackingPagePath.Lookup:
        return <Lookup />;
      case TrackingPagePath.Status:
        return <PageStatus />;
      case TrackingPagePath.Appearance:
        return <Appearance />;
      default:
        return null;
    }
  }, [currentPath]);

  const handleUploadImage = async () => {
    const requests = getMediaUploadRequests(trackingPageMediaData.data, trackingPageMediaData.oldData);
    await Promise.all(
      requests.map((req) => {
        if (req.isUrlOnly) {
          return updateUploadImage({ name: req.name, url: req.url ?? '' }).unwrap();
        }
        const formData = new FormData();
        formData.append('name', req.name);
        if (req.file) {
          formData.append('file', req.file);
        }
        if (req.url) {
          formData.append('url', req.url);
        }
        formData.append('shop', shop);
        formData.append('urlParams', token?.urlParams || '');
        return uploadImage(formData).unwrap();
      }),
    );
  };

  const handleDiscard = () => {
    dispatch(
      trackingPageSlice.actions.handleTrackingPageValue({
        ...trackingPage.oldValue,
      }),
    );
    dispatch(trackingPageSlice.actions.handleTrackingPageFieldsChange({}));
    dispatch(trackingPageSlice.actions.handleChangeValidation([]));

    dispatch(
      trackingPageSlice.actions.handleLanguageValue({
        ...language.oldValue.value,
      }),
    );
    dispatch(trackingPageSlice.actions.handleLanguageFieldsChange({}));
    dispatch(trackingPageSlice.actions.handleLanguageConfig({ ...language.oldValue.languageConfig }));
    dispatch(trackingPageSlice.actions.handleChangeValidationLanguage([]));

    dispatch(trackingPageMediaSlice.actions.handleUpdateMedia({ data: trackingPageMediaData.oldData }));
  };

  const handleSave = async () => {
    if (isLockedFeature) {
      dispatch(
        commonSlice.actions.handleToast({
          isOpen: true,
          error: true,
          content: 'This feature is not available on your current plan. Please upgrade to access this feature.',
        }),
      );
      return;
    }

    // Media is its own save path: the old code buried the toast + state reset
    // inside the trackingPage block, so a media-only save uploaded to the DB but
    // never showed a toast nor hid the save bar. The TRACKING_PAGE tag is
    // invalidated by the upload mutations, which refetches and resets the media
    // dirty state (hiding the save bar and loading the new image url).
    if (isEnableSaveMedia) {
      try {
        await handleUploadImage();
        dispatch(commonSlice.actions.handleToast({ isOpen: true, error: false, content: 'Save successfully' }));
        if (apiError) {
          setApiError('');
        }
      } catch (e) {
        setApiError(getErrorFromAPI(e));
      }
    }

    if (!isUndefinedArray(Object.values(trackingPage.fieldsChange)) || Object.entries(trackingPage.fieldsChange).length > 0) {
      updateTrackingPage(trackingPage.fieldsChange).then((res) => {
        if ('data' in res && res.data && res.data?.state === 1) {
          dispatch(trackingPageSlice.actions.handleTrackingPageFieldsChange({}));
          dispatch(trackingPageSlice.actions.handleTrackingPageOldValue(trackingPage.value));
          dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));

          // check show review table tab in sidebar
          if (trackingPage.fieldsChange?.reviewQuestions?.isActive !== accountState.isEnabledReview) {
            dispatch(
              planSlice.actions.handleChangePlan({
                ...accountState,
                isEnabledReview: trackingPage.fieldsChange?.reviewQuestions?.isActive,
              }),
            );
          }
          if (apiError) {
            setApiError('');
          }
          return;
        }
        setApiError(getErrorFromAPI(res));
      });
    }

    if (!isUndefinedArray(Object.values(language.fieldsChange)) || Object.entries(language.fieldsChange).length > 0) {
      updateTranslation(language.fieldsChange).then((res) => {
        if ('data' in res && res.data && res.data?.state === 1) {
          if (apiError) {
            setApiError('');
          }
          dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
          dispatch(trackingPageSlice.actions.handleLanguageFieldsChange({}));
          return;
        }
        setApiError(getErrorFromAPI(res));
      });
    }
  };

  const hasChange = useMemo(() => {
    return (
      Object.entries(trackingPage.fieldsChange).length > 0 ||
      Object.entries(language.fieldsChange).length > 0 ||
      isEnableSaveMedia
    );
  }, [language.fieldsChange, trackingPage.fieldsChange, isEnableSaveMedia]);
  const isDisableSave = useMemo(() => {
    return (
      !!trackingPage.validation.length ||
      !hasChange ||
      !shippingInfo.length ||
      !progressBar.length ||
      !!language.validation.length
    );
  }, [hasChange, language.validation.length, progressBar.length, shippingInfo.length, trackingPage.validation.length]);

  const isLoadingAction = useMemo(() => {
    return uploadData.isLoading || isLoadingUpdateTrackingPage || isLoadingUpdateTranslation;
  }, [isLoadingUpdateTrackingPage, isLoadingUpdateTranslation, uploadData.isLoading]);

  const actionGroup = [
    {
      content: 'Discard',
      onAction: handleDiscard,
      disabled: !hasChange,
      primary: false,
    },
    {
      content: 'Save',
      onAction: handleSave,
      primary: true,
      disabled: isDisableSave,
      loading: isLoadingAction,
    },
  ];

  const isLockedFeature = isLockFeature([AccountPlan.Starter]);

  return (
    <TrackingPageLayout fetchDataOnly>
      {/* The editor bypasses the app Layout (fetchDataOnly), so the global
          <CustomToast> isn't mounted here. Render it explicitly — it portals
          into AdminLayout's <Frame> — otherwise save toasts never show. */}
      <CustomToast />
      <ControlBar isFixed actionGroup={actionGroup} hidden={!hasChange} />
      <FullscreenBar onAction={() => navigate(PATH.TRACKING_PAGE)}>
        <FullscreenBarContainerStyled>
          <Text as="span">Synctrack Order Tracking</Text>
        </FullscreenBarContainerStyled>
      </FullscreenBar>
      <ContentContainerStyled>
        <ErrorBanner isVisible={!!apiError}>{apiError}</ErrorBanner>
        <Layout>
          <Layout.Section variant="oneThird">
            <div className="d-flex h-full">
              <div className="actions-bar">
                {navList.map((item, index) => {
                  return (
                    <Tooltip content={item.title} key={item.id}>
                      <div
                        className={`nav-icon ${currentPath.startsWith(item.path) ? 'nav-icon__active' : ''}`}
                        onClick={() => handleTabChange(item.path)}
                      >
                        <Icon source={item.icon} />
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
              <div className="content-left">
                {isLockedFeature ? (
                  <UpgradeCard
                    backgroundColor="#EAF4FF"
                    textColor="#00527C"
                    buttonLabel="Upgrade"
                    onButtonClick={() =>
                      navigateToPlan({
                        trigger_type: 'paywall',
                        trigger_element: 'Upgrade',
                        feature: 'customize_tracking_page',
                      })
                    }
                    message={
                      <Text as="p" fontWeight="bold">
                        You need to upgrade to the {getPlanName(AccountPlan.Basic)} or higher plan to customize your tracking page.
                      </Text>
                    }
                    style={{
                      borderRadius: '0',
                    }}
                  />
                ) : null}
                {renderContentLeft()}
              </div>
            </div>
          </Layout.Section>
          <Layout.Section>
            <div className="content-right">
              <div className="content-right-actions">
                <Select
                  label=""
                  options={[
                    { label: 'Lookup', value: TrackingPagePreviewPath.Lookup },
                    { label: 'Status', value: TrackingPagePreviewPath.Status },
                  ]}
                  value={currentPreviewPath}
                  onChange={(value) => {
                    dispatch(trackingPageSlice.actions.handleCurrentPreviewPath(value as TrackingPagePreviewPath));
                  }}
                />
                <ButtonGroup variant="segmented">
                  <Button
                    pressed={currentPreviewMode === Device.Desktop}
                    onClick={() => handleChangeViewMode(Device.Desktop)}
                    icon={DesktopIcon}
                  />
                  <Button
                    pressed={currentPreviewMode === Device.Mobile}
                    onClick={() => handleChangeViewMode(Device.Mobile)}
                    icon={MobileIcon}
                  />
                </ButtonGroup>
                <Button url={LINK.CUSTOMER_SHOP(shop, proxyPath)} icon={ViewIcon} variant="monochromePlain" removeUnderline>
                  Preview
                </Button>
              </div>
              <Suspense fallback={<Spinner size="large" />}>
                <PreviewContent />
              </Suspense>
            </div>
          </Layout.Section>
        </Layout>
      </ContentContainerStyled>
      <Modal
        title="You have unsaved changes"
        open={isShowConfirmModal}
        onClose={() => {
          setIsShowConfirmModal(false);
        }}
        primaryAction={{
          content: 'Leave page',
          onAction: () => {
            handleDiscard();
            setIsShowConfirmModal(false);
            navigate(PATH.TRACKING_PAGE);
          },
          // @ts-ignore
          tone: 'critical',
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setIsShowConfirmModal(false);
            },
          },
        ]}
      >
        <Modal.Section>
          <div id="est-modal-confirm">If you leave this page, all unsaved changes will be lost.</div>
        </Modal.Section>
      </Modal>
    </TrackingPageLayout>
  );
};

export default ModalTrackingPage;
